import { FC, useContext, useEffect, useState } from "react";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { useMutation } from "react-query";
import client from "../../api";
import { BankCard, PaginationInput, User } from "src/api/types";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import {
  capitalizeEachFirstLetter,
  capitalizeFirstLetter,
  convertChannelName,
  convertPaymentSystem,
  formatCardNumber,
  getCurrencyInfo,
  getErrorMessage,
  getTransactionStatusStyle,
  logger,
  makeNumberWithFloatingDigits,
} from "src/lib/utils";
import { Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import {
  BalanceDataType,
  BankCardDataType,
  TransactionDataType,
} from "src/lib/constants/table-type";
import { useModalAction } from "../modal-views/context";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import routes from "src/config/routes";

const DepositListPage = ({
  searchText,
  onSelectedInvoiceData,
  onSelectedUser,
  searchField,
  startDate,
  endDate,
  isBuisiness,
  timezone,
}: {
  searchText: string;
  onSelectedInvoiceData: any;
  onSelectedUser: any;
  searchField: string;
  startDate?: Date | null;
  endDate?: Date | null;
  isBuisiness?: boolean;
  timezone: string;
}) => {
  const navigate = useNavigate();

  const targetModule = isBuisiness
    ? permissionConstants.TRANSACTION_HISTORY_BUSINESS
    : permissionConstants.TRANSACTION_HISTORY_INDIVIDUAL;
  const { canView, canList, canDownload } = usePermissions(targetModule);
  const canViewTransactions = canView || canList;

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { openModal } = useModalAction();

  const [dataSource, setDataSource] = useState<TransactionDataType[]>([]);
  const [searchParams, setSearchParams] = useState<PaginationInput>({
    type: "deposit",
    per_page: LIST_PAGE_SIZE,
    current_page: 1,
  });

  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isMounted && canViewTransactions) {
        logger.log("called");
        getList({
          ...searchParams,
          search_value: searchText.length > 0 ? searchText : undefined,
          search_field: searchText.length > 0 ? searchField : undefined,
          start_date: startDate
            ? moment(startDate).format("yyyy-MM-DD")
            : undefined,
          end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
          timezone: timezone,
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    isMounted,
    canViewTransactions,
    searchParams.current_page,
    searchText,
    searchField,
    startDate,
    endDate,
    isBuisiness,
  ]);

  useEffect(() => {
    setSearchParams({
      ...searchParams,
      current_page: 1,
    });
  }, [searchText]);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  const onSelectUser = (userID: number) => {
    if (!canView) return;
    navigate(`/${isBuisiness ? "users-business" : "users"}/${String(userID)}`, {
      replace: true,
    });
  };

  const onPageChange = (page: number, pageSize: number) => {
    logger.log(page, pageSize);
    setSearchParams({
      ...searchParams,
      current_page: page,
    });
  };

  const { mutate: getList, isLoading } = useMutation(
    isBuisiness
      ? client.transactions.buisinessDeposit
      : client.transactions.individualDeposit,
    {
      onSuccess: (data) => {
        logger.log("datadatadata", data);
        setTotal(data.total);

        setDataSource(
          data.data.map((item) => {
            const isCartuBoxo =
              item.payment_system === "cartubank" &&
              item.external_processing_system === "boxo_pay";
            const isIncognitoCard =
              item.payment_system === "tbc_payments" &&
              item.external_processing_system === "incognito_boxo_pay";
            return {
              key: item.id,
              user_id: item.user_id,
              asset_id: item.asset_id,

              //  Amount handling
              amount: item.amount,

              //  Fee handling
              amount_fee: item.amount_fee,

              //  Indicated amount logic
              indicated_amount:
                // isCartuBoxo || isIncognitoCard
                // ?
                // item.paid_amount,
                // :
                item.indicated_amount,

              total_amount:
                // isCartuBoxo || isIncognitoCard
                item.amount,
              // : item.total_amount,
              payment_system: item.payment_system,
              external_processing_system: item.external_processing_system,

              channel_name:
                item.payment_system === "self_terminal"
                  ? item.channel_name
                  : item?.channel_name === "admin_panel"
                    ? "Fastoo"
                    : item?.channel_name == "incognito"
                      ? item?.channel_name
                      : item.payment_system === "paypal"
                        ? "PayPal"
                        : item.payment_system === "cartubank"
                          ? item?.channel_name
                          : item?.type === "withdraw"
                            ? item?.requisites?.beneficiary_bank
                            : item?.sender_bank_name,

              bank_account:
                item?.type === "withdraw"
                  ? item?.requisites?.iban
                  : (item?.iban ?? "__"),

              card_number: formatCardNumber(item?.cards?.card_number || ""),
              status: item.status,
              sender_bank_code: item.sender_bank_code,
              sender_bank_name: item.sender_bank_name,
              iban: item.iban,
              type: item.type,
              description:
                item.payment_system === "cartubank"
                  ? "N/A"
                  : item.description_text,
              comment: item.comment,
              to: item.to,
              withdraw_payment_system: item.withdraw_payment_system,
              refund_id: item.refund_id,
              created_at: item.created_at,
              updated_at: item.updated_at,
              asset_code: item.asset.code,
              invoice: !!item.requisites,
              invoice_data: item.requisites,
              full_name: item.full_name ?? "__",
              payment_reference: item?.user?.payment_reference ?? "__",
              ...(isBuisiness
                ? { taxCode: item?.user?.aml_business_data?.tax_code ?? "__" }
                : { tin: item?.user?.aml_user_data?.tin ?? "__" }),
            };
          }),
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const onViewInvoice = (key: number) => {
    if (!canView) return;
    const filterInvoices = dataSource.filter((item) => item.key === key);
    if (filterInvoices.length === 0) {
      return;
    }

    const selInvoice = filterInvoices[0];
    onSelectedInvoiceData(selInvoice);
    /*
    setInvoiceFromPage(routes.transactions);
    selInvoice.invoice_data &&
      navigate(routes.invoice, { state: { info: selInvoice } });
      */
  };

  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const { mutate: downloadReceipt, isLoading: downloadReceiptLoading } =
    useMutation(client.transactions.downloadTransactionReceiptPdf, {
      onSuccess: (pdfBlob, variables) => {
        const blob = new Blob([pdfBlob], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `invoice_${variables.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();

        setAlertText("Receipt downloaded successfully");
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          setAlertText(
            error.response
              ? getErrorMessage(error.response.data)
              : APISomethingWrongMsg,
          );
        }
        setIsAlertOpened(true);
      },
    });

  const receiptBtn = (record: any) => {
    if (!canDownload) {
      setAlertText("You do not have permission to download receipt");
      setIsAlertOpened(true);
      return;
    }
    logger.log("RECORD", record);
    setDownloadingId(record.key);
    downloadReceipt(
      {
        type: "topup",
        id: record.key,
        timezone: timezone,
      },
      {
        onSettled: () => setDownloadingId(null),
      },
    );
  };

  const changeName = (name: string) => {
    return name === "cartubank" ? "Card" : name;
  };

  const columns: ColumnsType<TransactionDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "key",
      className: "w-auto",
      key: "key",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Reference
        </p>
      ),
      dataIndex: "payment_reference",
      className: "w-auto",
      key: "payment_reference",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          User ID
        </p>
      ),
      dataIndex: "user_id",
      key: "user_id",
      className: "w-auto",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          {canView ? (
            <a
              className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline cursor-pointer"
              onClick={() => onSelectUser(value)}
            >
              {value}
            </a>
          ) : (
            <span className="font-manrope text-secondary text-[14px] font-medium">
              {value}
            </span>
          )}
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Currency
        </p>
      ),
      dataIndex: "asset_code",
      className: "w-auto",
      key: "asset_code",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value.toUpperCase()}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Full name
        </p>
      ),
      dataIndex: "full_name",
      className: "w-auto",
      key: "full_name",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {isBuisiness ? "Tax Code" : "ID Number"}
        </p>
      ),
      dataIndex: isBuisiness ? "taxCode" : "tin",
      className: "w-auto",
      key: isBuisiness ? "taxCode" : "tin",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Indicated Amount
        </p>
      ),
      dataIndex: "indicated_amount",
      key: "indicated_amount",
      className: "w-auto",
      sorter: (a, b) => a.indicated_amount - b.indicated_amount,
      render: (value: number) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Fee
        </p>
      ),
      dataIndex: "amount_fee",
      className: "w-auto",
      key: "amount_fee",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {makeNumberWithFloatingDigits(value, 2)}
          </p>
        </div>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Amount
        </p>
      ),
      dataIndex: "total_amount",
      key: "total_amount",
      className: "w-auto",
      render: (value: number) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {makeNumberWithFloatingDigits(value, 2)}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Payment Method
        </p>
      ),
      dataIndex: "payment_system",
      className: "w-auto",
      key: "payment_system",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {convertPaymentSystem(changeName(value))}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Channel Name
        </p>
      ),
      dataIndex: "channel_name",
      className: "w-auto",
      key: "channel_name",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {convertChannelName(value) || "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Bank Account
        </p>
      ),
      dataIndex: "bank_account",
      className: "w-auto",
      key: "bank_account",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value || "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Card Number
        </p>
      ),
      dataIndex: "card_number",
      className: "w-auto",
      key: "card_number",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] whitespace-nowrap sm:text-[14px] font-medium">
            <span className="text-[#000]">
              {value === "__" ? "__" : value}{" "}
            </span>
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Invoice
        </p>
      ),
      dataIndex: "invoice",
      className: "w-auto",
      render: (
        _,
        record: { key: React.Key; invoice: boolean; invoice_data?: any },
      ) => (
        <div
          className={`${
            !record.invoice ? "invisible" : ""
          } flex flex-row gap-4`}
        >
          {canView ? (
            <a
              className="text-secondary underline hover:underline hover:text-primary text-[12px] font-manrope font-medium cursor-pointer"
              onClick={() => onViewInvoice(record.key as number)}
            >
              Invoice
            </a>
          ) : (
            <span className="text-gray-400 text-[12px] font-manrope font-medium">
              Invoice
            </span>
          )}
        </div>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Status
        </p>
      ),
      dataIndex: "status",
      className: "w-auto",
      key: "status",
      render: (value, data) => (
        <div className="flex flex-row gap-2 items-center rounded-full">
          <p
            className={`font-manrope text-[12px] font-medium px-2 rounded-full ${getTransactionStatusStyle(
              value,
            )}`}
          >
            {capitalizeFirstLetter(value)}
          </p>
          {(data?.comment || data?.message) &&
          (value === "canceled" || value === "waiting") ? (
            <Tooltip title={data?.comment || data?.message}>
              <span className="cursor-pointer">{infoIcon}</span>
            </Tooltip>
          ) : null}
        </div>
      ),
    },

    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       Comment
    //     </p>
    //   ),
    //   dataIndex: "comment",
    //   className: "w-auto",
    //   key: "comment",
    //   render: (value) => (
    //     <div className="flex flex-row gap-2 items-center rounded-full">
    //       <p
    //         className={`font-manrope text-[12px] font-medium px-2 rounded-full text-white}`}
    //       >
    //         {value ?? "__"}
    //       </p>
    //     </div>
    //   ),
    // },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Description
        </p>
      ),
      dataIndex: "description",
      className: "w-auto",
      key: "description",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center rounded-full">
          <p
            className={`font-manrope text-[12px] font-medium px-2 rounded-full text-white}`}
          >
            {capitalizeFirstLetter(value) ?? "__"}
          </p>
        </div>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created
        </p>
      ),
      dataIndex: "created_at",
      className: "w-auto",
      key: "created_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {moment.unix(value).utc(true).format("DD MMM YYYY H:mm z")}
            {moment.unix(value).utcOffset(240).format("DD/MM/YYYY H:mm")} */}
            {moment.unix(value).local().format("DD/MM/YYYY H:mm")}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Updated
        </p>
      ),
      dataIndex: "updated_at",
      className: "w-auto",
      key: "updated_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {moment.unix(value).utc(true).format("DD MMM YYYY H:mm z")} */}
            {/* {moment.unix(value).utcOffset(240).format("DD/MM/YYYY H:mm")} */}
            {moment.unix(value).local().format("DD/MM/YYYY H:mm")}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Transaction Receipt
        </p>
      ),
      dataIndex: "invoice",
      className: "w-auto",
      render: (
        _,
        record: {
          key: React.Key;
          invoice: boolean;
          invoice_data?: any;
          type: string;
          status: string;
        },
      ) => (
        <div className="flex flex-row gap-4 items-center">
          {record.status !== "completed" || !canDownload ? (
            "___"
          ) : downloadingId === Number(record.key) ? (
            <p className="text-gray-400 text-[12px] font-manrope italic">
              Downloading...
            </p>
          ) : (
            <a
              className="text-secondary underline hover:underline hover:text-primary text-[12px] font-manrope font-medium cursor-pointer"
              onClick={() => receiptBtn(record)}
            >
              Receipt
            </a>
          )}
        </div>
      ),
    },
  ];

  const mobileColumns: ColumnsType<TransactionDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Info
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData = dataSource.filter((item) => item.key === record.key)[0];

        return (
          <div className="w-full flex flex-row gap-4 items-start justify-between">
            <div className="w-full flex flex-col items-start gap-2">
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  ID
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {selData ? String(selData.key) : "-"}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Reference
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {selData ? String(selData.payment_reference) : "-"}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Asset
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium text-">
                  {selData.asset_code.toUpperCase()}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Full name
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium text-">
                  {selData.full_name}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Amount
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.total_amount, 2)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Fee
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {makeNumberWithFloatingDigits(selData.amount_fee, 2)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Status
                </p>
                <p
                  className={`font-manrope text-[124x] font-medium px-2 rounded-full ${getTransactionStatusStyle(
                    selData.status,
                  )}`}
                >
                  {capitalizeFirstLetter(selData.status)}
                </p>
              </div>
            </div>
            <div className="w-full flex flex-col items-start gap-2">
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Created
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {/* {moment
                    .unix(selData.created_at)
                    .utc(true)
                    .format("DD MMM YYYY HH:mm z")} */}
                  {/* {moment
                    .utc(selData.created_at)
                    .utcOffset(240)
                    .format("DD/MM/YYYY H:mm")} */}
                  {moment
                    .unix(selData.created_at)
                    .local()
                    .format("DD/MM/YYYY H:mm")}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Updated
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {/* {moment
                    .unix(selData.updated_at)
                    .utcOffset(240)
                    .format("DD/MM/YYYY H:mm")} */}
                  {moment
                    .unix(selData.updated_at)
                    .local()
                    .format("DD/MM/YYYY H:mm")}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  User ID
                </p>
                {canView ? (
                  <a
                    className="font-manrope text-secondary text-[14px] font-medium underline hover:text-primary hover:underline text-right cursor-pointer"
                    onClick={() => onSelectUser(selData.user_id)}
                  >
                    {selData.user_id}
                  </a>
                ) : (
                  <span className="font-manrope text-secondary text-[14px] font-medium text-right">
                    {selData.user_id}
                  </span>
                )}
              </div>{" "}
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Indicated am.
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium ">
                  {makeNumberWithFloatingDigits(selData.indicated_amount, 2)}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Payment Method
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {convertPaymentSystem(changeName(selData.payment_system))}
                </p>
              </div>
              <div
                className={`${
                  !selData.invoice ? "hidden" : ""
                } w-full flex flex-row items-center justify-between gap-2`}
              >
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Invoice
                </p>

                {canView ? (
                  <a
                    className="text-primary underline hover:underline hover:text-secondary text-[14px] font-manrope font-medium cursor-pointer"
                    onClick={() => onViewInvoice(selData.key as number)}
                  >
                    Invoice
                  </a>
                ) : (
                  <span className="text-gray-400 text-[14px] font-manrope font-medium text-right">
                    Invoice
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  if (!canViewTransactions) {
    return (
      <div className="custom-scroll bg-white rounded-r-[12px] rounded-bl-[12px] w-full p-6 text-center text-secondary">
        You do not have permission to view transactions.
      </div>
    );
  }

  return (
    <div className="custom-scroll bg-white rounded-r-[12px] rounded-bl-[12px] w-full relative">
      <div className="p-4 relative">
        <Table
          loading={isLoading}
          showSorterTooltip={false}
          columns={isMobile ? mobileColumns : columns}
          dataSource={dataSource}
          pagination={{
            total,
            current: searchParams.current_page,
            onChange: onPageChange,
            position: ["bottomCenter"],
            defaultPageSize: LIST_PAGE_SIZE,
            showSizeChanger: false,
          }}
        />
      </div>
    </div>
  );
};

export default DepositListPage;

const infoIcon = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke="black"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M12 16V12M12 8H12.01"
      stroke="black"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);
